import type { ReceiveMessageRequest, Message } from '@aws-sdk/client-sqs';
import { SQS } from '@aws-sdk/client-sqs';
import { awaitAllSafely, isEmptyArray, isNotEmptyArray, isNotNullOrUndefined } from '@weft/utils';
import { DEFAULT_MAX_NUMBER_OF_MESSAGES, DEFAULT_WAIT_TIME_SECONDS } from './read-batch-messages-constants';
import type { ReadFromQueueOptions, QueueProcessOperation } from './read-batch-messages-types';
export type { ReadFromQueueOptions, QueueProcessOperation } from './read-batch-messages-types';

const sqs = new SQS();

export async function processMessagesFromQueue<FormattedMessage>(
  queueUrl: string,
  operation: QueueProcessOperation<FormattedMessage>,
  options: ReadFromQueueOptions = {},
) {
  const messages = await readMessageFromQueue(queueUrl, options);

  if (isEmptyArray(messages)) {
    return;
  }

  const processingMessages = messages.map((message) => processMessage(queueUrl, message, operation));

  await awaitAllSafely(...processingMessages);
}

async function processMessage<FormattedMessage>(queueUrl: string, message: Message, operation: QueueProcessOperation<FormattedMessage>) {
  const formattedMessage = operation.format(message);

  if (isNotNullOrUndefined(formattedMessage)) {
    await operation.run(formattedMessage);
  }

  await sqs.deleteMessage({
    QueueUrl: queueUrl,
    ReceiptHandle: message.ReceiptHandle,
  });
}

async function readMessageFromQueue(queueUrl: string, options: ReadFromQueueOptions = {}) {
  const { maxNumberOfMessages, waitTimeSeconds } = {
    maxNumberOfMessages: DEFAULT_MAX_NUMBER_OF_MESSAGES,
    waitTimeSeconds: DEFAULT_WAIT_TIME_SECONDS,
    ...options,
  };

  const params: ReceiveMessageRequest = {
    QueueUrl: queueUrl,
    MaxNumberOfMessages: maxNumberOfMessages,
    WaitTimeSeconds: waitTimeSeconds,
  };

  const data = await sqs.receiveMessage(params);

  if (data.Messages && data.Messages.length > 0) {
    return data.Messages;
  }

  return isNotEmptyArray(data.Messages) ? data.Messages : null;
}